import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import {
  Button,
  Card,
  RadioButtonGroup,
  Radio,
  MediaObject,
  Tooltip
} from '@salesforce/design-system-react';

import { urlForFilter } from '../../actions/analytics';
import { default as config } from '../../../utilities/config';
import * as Util from '../../../common/utilities';

import {
  AssetBrowser,
  AttributeChartFilter,
  AttributeTable,
  AttributeValueChart,
  PerformanceChart
} from '../';

import './PerformanceDetails.scss';

class PerformanceDetails extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selection: [],
      chartToggle: 'clicks',
      sortColumn: { property: '', sortDirection: '' }
    };
  }

  componentDidMount() {
    const assets = this.assetPerformers();
    if (assets.length > 0 && this.state.selection.length === 0) {
      // Select the first 10 performers by default
      this.setState({
        selection: assets.slice(0, this.maxSelected())
      });
    }
  }

  componentDidUpdate(prevProps) {
    //utilpropchanged returns true if it changed
    if (Util.propChanged(this.props, prevProps, 'performance.performers')) {
      const assets = this.assetPerformers();
      if (assets.length > 0 && this.keysChanged(prevProps)) {
        // Select the top performers by default
        this.defaultSelection();
      }
    }
  }

  extractKey(performer) {
    return Object.values(performer)[0];
  }

  keysChanged(prevProps) {
    let currentKeys = this.props.performance.performers.map(perf =>
      this.extractKey(perf)
    );
    let prevKeys = prevProps.performance.performers.map(perf =>
      this.extractKey(perf)
    );
    return prevKeys.length === 0
      ? true
      : currentKeys.length !== prevKeys.length ||
          !prevKeys.every(elem => currentKeys.indexOf(elem) > -1);
  }

  defaultSelection() {
    const assets = this.assetPerformers();
    if (assets.length > 0) {
      // Select the top performers by default
      this.setState({
        selection: assets.slice(0, this.maxSelected())
      });
    }
  }

  handleSort(sort) {
    this.setState({ sortColumn: { ...sort } });
    const { performers } = this.props.performance;
    const sortBy = sort.property;
    if (sort.sortDirection === 'desc') {
      performers.sort((a, b) => parseFloat(a[sortBy]) - parseFloat(b[sortBy]));
    } else {
      performers.sort((a, b) => parseFloat(b[sortBy]) - parseFloat(a[sortBy]));
    }
    this.defaultSelection();
  }

  assetPerformers() {
    const { performers } = this.props.performance;
    const { distribution, filter } = this.props;
    const keys = [];
    return performers.map((performer, idx) => {
      if (performer?.asset?.custom) {
        Object.keys(performer.asset.custom).forEach(col => {
          if (!keys.includes(col)) {
            keys.push(col);
          }
        });
      }
      const merged = {
        ...performer,
        ...performer.asset,
        dot: idx + 1
      };

      if (distribution && filter.filter === 'ect_tags') {
        merged.nrOfAssets = distribution[performer['tag']]?.total;
      } else if (
        distribution &&
        (performer[filter.filter] || performer[filter.filter] === null)
      ) {
        const node = performer[filter.filter] || '_null';
        merged.nrOfAssets = distribution[node].total;
      }

      delete merged.asset;
      return merged;
    });
  }

  maxSelected() {
    if (this.props.filter?.filterBy === 'assetAttribute') return 20;
    return 10;
  }

  handleRowChanged = (event, { selection }) => {
    if (selection.length <= this.maxSelected() && selection.length > 0) {
      this.setState({ selection });
    }
  };

  header(empty) {
    const { formatMessage } = this.props.intl;
    const { filter } = this.props;
    const noDataText = formatMessage({
      id: 'components.ecs.analytics.perf.noData'
    });

    if (
      empty &&
      !this.props.performance.filterWasModified &&
      filter.filterBy !== 'assetAttribute'
    ) {
      return (
        <div className="performance-header-left">
          <span className="header">{noDataText}</span>
        </div>
      );
    }

    const attributesHeaderRight = (
      <div className="performance-header-right attribute-header-right">
        <RadioButtonGroup
          className="inline"
          onChange={event =>
            this.props.actions.modifyAttributeChart(event.target.value)
          }
        >
          <Radio
            key="assets"
            id="assets"
            labels={{
              label: formatMessage({
                id: 'components.ecs.analytics.assetAttribute.nrOfAssets'
              })
            }}
            value="nrOfAssets"
            checked={this.props.attributeRadio === 'nrOfAssets'}
            variant="button-group"
          />
          <Radio
            key="selections"
            id="selections"
            labels={{
              label: formatMessage({
                id: 'components.ecs.analytics.assetAttribute.selections'
              })
            }}
            value="selections"
            checked={this.props.attributeRadio === 'selections'}
            variant="button-group"
          />
          <Radio
            key="unique-clicks"
            id="unique-clicks"
            labels={{
              label: formatMessage({
                id: 'components.ecs.analytics.assetAttribute.clicks'
              })
            }}
            value="clicks"
            checked={this.props.attributeRadio === 'clicks'}
            variant="button-group"
          />
        </RadioButtonGroup>
      </div>
    );

    const reportFilter = { ...this.props.filter, top: 0 };
    const reportUrl =
      config.serverUrl['metalMastodon'] +
      urlForFilter(reportFilter) +
      '&report=true';
    const downloadReportButton = (
      <a href={reportUrl}>
        <Tooltip
          align="left"
          content={formatMessage({
            id: 'components.ecs.analytics.assetAttribute.downloadReportTooltip'
          })}
        >
          <Button
            id="download-report-button"
            iconCategory="utility"
            iconName="download"
            iconVariant="border-filled"
            variant="icon"
          />
        </Tooltip>
      </a>
    );

    const performanceHeaderRight = (
      <div className="performance-header-right">
        {downloadReportButton}
        <div className="chart-toggle">
          <RadioButtonGroup
            className="chart-type-radio"
            onChange={event => {
              this.setState({ chartToggle: event.target.value });
            }}
          >
            <Radio
              key="ctor"
              id="ctor"
              labels={{
                label: formatMessage({
                  id: 'components.ecs.analytics.perf.clickChart.toggle.ctor'
                })
              }}
              value="ctor"
              checked={this.state.chartToggle === 'ctor'}
              variant="button-group"
            />
            <Radio
              key="clicks"
              id="clicks"
              labels={{
                label: formatMessage({
                  id: 'components.ecs.analytics.perf.clickChart.toggle.clicks'
                })
              }}
              value="clicks"
              checked={this.state.chartToggle === 'clicks'}
              variant="button-group"
            />
          </RadioButtonGroup>
        </div>
      </div>
    );

    return (
      <div className="performance-header slds-grid slds-grid_vertical-align-center">
        <div className="performance-header-left slds-col_bump-right">
          {filter.filterBy !== 'assetAttribute' ? (
            <span className="header">
              {formatMessage({
                id: 'components.ecs.analytics.perf.chartTitle'
              })}
            </span>
          ) : (
            <div>
              <span className="header">
                {formatMessage({
                  id: 'components.ecs.analytics.assetAttribute.chartTitle'
                })}
              </span>
              <AttributeChartFilter
                filter={filter}
                attributeFilters={this.props.attributeFilters}
                assetKeys={this.props.assetKeys}
                attributeValues={this.props.attributeValues}
                actions={this.props.actions}
                rules={this.props.rules}
              />
            </div>
          )}
        </div>
        {filter.filterBy !== 'assetAttribute'
          ? performanceHeaderRight
          : attributesHeaderRight}
      </div>
    );
  }

  columns() {
    const cols = [
      { name: 'thumbnail', sortable: false, width: '62px' },
      { name: 'key', sortable: false, width: '6.25rem' },
      {
        name: 'selections',
        sortable: true,
        numberStyle: { style: 'decimal' },
        width: '6.25rem'
      },
      {
        name: 'ctor',
        sortable: true,
        numberStyle: { style: 'percent', maximumFractionDigits: 2 },
        width: '6.25rem'
      },
      {
        name: 'clicks',
        sortable: true,
        numberStyle: { style: 'decimal' },
        width: '6.25rem'
      }
    ];

    if (this.props.filter?.filter) {
      let assetClassName = this.props.filter.filter;
      if (this.props.filter.filterBy === 'jobId') {
        assetClassName = this.props.filter.block?.asset_type;
      }
      const assetClass = this.props.assetTypes.find(
        type => type.name === assetClassName
      );
      if (assetClass?.control_subject_line) {
        cols.push(
          {
            name: 'send_time_selections',
            sortable: false,
            numberStyle: { style: 'decimal' },
            width: '6.25rem'
          },
          {
            name: 'open_rate',
            sortable: false,
            numberStyle: { style: 'percent', maximumFractionDigits: 2 },
            width: '6.25rem'
          },
          { name: 'subject_line', sortable: false, width: '11rem' },
          { name: 'preheader', sortable: false, width: '11rem' }
        );
      }
    }

    return cols;
  }

  trimDetails(details) {
    const firstData = details.findIndex(detail => detail?.data.length > 0);
    if (firstData > 0) {
      return details.slice(firstData - 1);
    } else {
      return details;
    }
  }

  render() {
    const { filter } = this.props;
    const isHourly = filter.endDate - filter.startDate <= 172800000;
    const details = isHourly
      ? this.trimDetails(this.props.performance.details)
      : this.props.performance.details;
    const keys = [];
    const assets = this.assetPerformers();
    const selectedKeys = this.state.selection.map(selected =>
      this.extractKey(selected)
    );
    const selectedPerformers = assets.filter(performer =>
      selectedKeys.includes(this.extractKey(performer))
    );
    const count = this.state.selection.length || 0;
    const maxSelected = this.maxSelected();
    keys.sort();

    const noDetails = (
      <div style={{ height: '50px', marginLeft: '1rem' }}>
        <FormattedMessage id="components.ecs.analytics.perf.noDataDetails" />
      </div>
    );

    const charts = (
      <React.Fragment>
        <div className="charts-card">
          <PerformanceChart
            details={details}
            performers={selectedPerformers}
            filter={filter}
            chartToggle={this.state.chartToggle}
          />
        </div>
      </React.Fragment>
    );

    const assetComponents = (
      <React.Fragment>
        {charts}
        <div className="items-selected">
          <FormattedMessage
            id="components.ecs.analytics.perf.items"
            values={{ count, maxAssets: maxSelected }}
          />
        </div>
        <AssetBrowser
          assets={assets}
          keys={keys}
          columns={this.columns()}
          showActions={false}
          popoverOverflow={assets.length < 12}
          className="highPerformers"
          selection={selectedPerformers}
          rowSelectionMode="checkbox"
          onRowSelect={this.handleRowChanged}
          sortColumn={this.state.sortColumn}
          onSort={this.handleSort.bind(this)}
          sortable
        />
      </React.Fragment>
    );

    const attributeComponents = (
      <React.Fragment>
        <AttributeValueChart
          attributes={selectedPerformers}
          measure={this.props.attributeRadio}
        />
        <AttributeTable
          attributes={assets}
          maxSelected={maxSelected}
          selection={selectedPerformers}
          onRowSelect={this.handleRowChanged}
        />
      </React.Fragment>
    );

    const detailsComponents =
      filter.filterBy !== 'assetAttribute'
        ? assetComponents
        : attributeComponents;

    return (
      <Card
        className="performance-details"
        header={<MediaObject body={this.header(!assets.length)} />}
      >
        {assets.length ? detailsComponents : noDetails}
      </Card>
    );
  }
}

PerformanceDetails.propTypes = {
  actions: PropTypes.object,
  assetTypes: PropTypes.array,
  performance: PropTypes.object,
  rules: PropTypes.object,
  filter: PropTypes.object.isRequired,
  location: PropTypes.object
};

export default injectIntl(PerformanceDetails);
